/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import { jsx } from '@atlaskit/css';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';

import { BaseConfluenceSpaceSelectorInner } from '../base-space-selector';
import type { ConfluenceSpaceSelectorProps } from '../base-space-selector/types';

const HOOK_OUTPUT_PROPS = [
	'listEntities',
	'fetchRecommendedSpaces',
	'fetchSpaceApprovalsConfigs',
] as const;

const omitHookOutputProps = <T extends Record<string, unknown>>(
	props: T,
	keys: readonly string[],
): Record<string, unknown> =>
	Object.fromEntries(Object.entries(props).filter(([key]) => !keys.includes(key)));

/**
 * Memoized version of BaseConfluenceSpaceSelector with custom comparison function.
 */
const BaseConfluenceSpaceSelector = memo(BaseConfluenceSpaceSelectorInner, (prevProps, nextProps) =>
	shallowEqualObjects(
		omitHookOutputProps(prevProps, HOOK_OUTPUT_PROPS),
		omitHookOutputProps(nextProps, HOOK_OUTPUT_PROPS),
	),
);

/**
 * AFM consumers should use this web-specific component
 */
export const ConfluenceSpaceSelector = ({
	cloudId,
	userId,
	fetchHomepage,
	recommendedSpaces,
	...props
}: ConfluenceSpaceSelectorProps): JSX.Element => {
	const { listEntities, fetchRecommendedSpaces, fetchSpaceApprovalsConfigs } =
		useConfluenceEntities(cloudId, {
			fetchHomepage,
		});
	return (
		<BaseConfluenceSpaceSelector
			userId={userId}
			recommendedSpaces={recommendedSpaces}
			listEntities={listEntities}
			fetchRecommendedSpaces={fetchRecommendedSpaces}
			fetchSpaceApprovalsConfigs={fetchSpaceApprovalsConfigs}
			{...props}
		/>
	);
};
